import api from "@/api";
import converters from "@/converters";
import type { ExternalDepartmentFragment } from "@/graphql";
import { Module_Type } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData } from "@/interfaces";
import { computed, ref } from "vue";

const DEFAULT_DATA: ICachedData<ExternalDepartmentFragment[]> = {
  status: "none",
  data: [],
};

const externalDepartments = ref<
  Record<Module_Type, ICachedData<ExternalDepartmentFragment[]>>
>({
  [Module_Type.Activity]: { ...DEFAULT_DATA },
  [Module_Type.Booking]: { ...DEFAULT_DATA },
  [Module_Type.Gallery]: { ...DEFAULT_DATA },
  [Module_Type.Groups]: { ...DEFAULT_DATA },
  [Module_Type.Library]: { ...DEFAULT_DATA },
  [Module_Type.Meal]: { ...DEFAULT_DATA },
  [Module_Type.Meeting]: { ...DEFAULT_DATA },
  [Module_Type.MyDay]: { ...DEFAULT_DATA },
  [Module_Type.News]: { ...DEFAULT_DATA },
  [Module_Type.Profiles]: { ...DEFAULT_DATA },
  [Module_Type.WhatsHappening]: { ...DEFAULT_DATA },
  [Module_Type.WorkSchedule]: { ...DEFAULT_DATA },
});

export function useExternalDepartments(type: Module_Type) {
  const init = async () => {
    const item = externalDepartments.value[type];

    // We should fetch only once
    if (item.status === "loaded") {
      return;
    }

    item.status = "loading";
    const data = await api.departments.getExternalDepartments(type);

    item.data = data.institutions?.getMyInstitution.externalDepartments || [];
    item.status = "loaded";
  };

  init();

  const externalDepartmentList = computed(() => {
    return externalDepartments.value[type].data.map((item) => {
      return converters.externalDepartments.toMultiSelectItem(item);
    });
  });

  const externalDepartmentOptions = computed(() => {
    return externalDepartments.value[type].data.map((item) => {
      return converters.externalDepartments.toOption(item);
    });
  });

  const loading = computed(() => {
    return externalDepartments.value[type].status === "loading";
  });

  const status = computed(() => {
    return externalDepartments.value[type].status;
  });

  const isReady = () => isDataReady(status);

  return {
    externalDepartments,
    externalDepartmentList,
    externalDepartmentOptions,
    loading,
    isReady,
  };
}
